import React, { useState, useMemo, useEffect } from 'react';
import useSWR from 'swr';
import api, { swrFetcher, testExchangeConnection } from '../services/apiClient';
import { 
  Copy, Pencil, Plus, X, RefreshCw, 
  ChevronDown, CheckCircle
} from 'lucide-react';
import ExchangeConfigModal from './ExchangeConfigModal';
import InfraConfigModal from './InfraConfigModal';
import './WhitelistManager.css';

// Helper to shorten address
const shortenAddress = (addr, start = 6, end = 4) => {
  if (!addr || addr.length < start + end + 3) return addr;
  return `${addr.slice(0, start)}...${addr.slice(-end)}`;
};

// Whitelist Configuration removed - all settings are now editable through
// Treasury Wallets and Exchange API Keys sections above

// Exchange field labels
const FIELD_LABELS = {
  apiKey: 'API Key',
  secretKey: 'Secret Key',
  passphrase: 'Passphrase',
  accountAddress: 'Account Address',
  walletAddress: 'Wallet Address',
  privateKey: 'Private Key',
  apiKeyIndex: 'API Key Index',
  accountIndex: 'Account Index',
  publicKey: 'Public Key',
  starkKeyPublic: 'Stark Key Public',
  starkKeyPrivate: 'Stark Key Private',
  vaultNumber: 'Vault Number',
  clientId: 'Client ID',
};

// Chain options for deposit addresses
const CHAIN_OPTIONS = [
  { id: 'arbitrum', label: 'Arbitrum', placeholder: '0x...' },
  { id: 'ethereum', label: 'Ethereum', placeholder: '0x...' },
  { id: 'solana', label: 'Solana', placeholder: 'Solana address...' },
  { id: 'bitcoin', label: 'Bitcoin', placeholder: 'Bitcoin address...' },
];

// Copy to clipboard helper
const copyToClipboard = (text, label = 'Value') => {
  navigator.clipboard.writeText(text).then(() => {
    console.log(`${label} copied to clipboard`);
  }).catch(err => {
    console.error('Failed to copy:', err);
  });
};

// Default exchange placeholders (always show these even if API fails)
const DEFAULT_EXCHANGES = {
  binance: {
    enabled: false,
    hasApiKey: false,
    fields: {
      apiKey: { configured: false, masked: null },
      secretKey: { configured: false, masked: null },
    },
    depositAddress: null,
  },
  okx: {
    enabled: false,
    hasApiKey: false,
    fields: {
      apiKey: { configured: false, masked: null },
      secretKey: { configured: false, masked: null },
      passphrase: { configured: false, masked: null },
    },
    depositAddress: null,
  },
  bybit: {
    enabled: false,
    hasApiKey: false,
    fields: {
      apiKey: { configured: false, masked: null },
      secretKey: { configured: false, masked: null },
    },
    depositAddress: null,
  },
  hyperliquid: {
    enabled: false,
    hasApiKey: false,
    fields: {
      accountAddress: { configured: false, masked: null },
      secretKey: { configured: false, masked: null },
      walletAddress: { configured: false, masked: null },
    },
    depositAddress: null,
  },
  lighter: {
    enabled: false,
    hasApiKey: false,
    fields: {
      apiKeyIndex: { configured: false, masked: null },
      accountIndex: { configured: false, masked: null },
      publicKey: { configured: false, masked: null },
      privateKey: { configured: false, masked: null },
    },
    depositAddress: null,
  },
  extended: {
    enabled: false,
    hasApiKey: false,
    fields: {
      apiKey: { configured: false, masked: null },
      starkKeyPublic: { configured: false, masked: null },
      starkKeyPrivate: { configured: false, masked: null },
      vaultNumber: { configured: false, masked: null },
      clientId: { configured: false, masked: null },
    },
    depositAddress: null,
  },
  kucoin: {
    enabled: false,
    hasApiKey: false,
    fields: {
      apiKey: { configured: false, masked: null },
      secretKey: { configured: false, masked: null },
      passphrase: { configured: false, masked: null },
    },
    depositAddress: null,
  },
  gateio: {
    enabled: false,
    hasApiKey: false,
    fields: {
      apiKey: { configured: false, masked: null },
      secretKey: { configured: false, masked: null },
    },
    depositAddress: null,
  },
  woox: {
    enabled: false,
    hasApiKey: false,
    fields: {
      apiKey: { configured: false, masked: null },
      secretKey: { configured: false, masked: null },
    },
    depositAddress: null,
  },
};

// Exchange Configuration Component - Simplified Tiles with Modal
function ExchangeConfig() {
  const { data: credentialsResponse, isLoading: loadingCreds, error: credsError, mutate: mutateCreds } = useSWR('/admin/exchanges', swrFetcher, {
    refreshInterval: 0,
    revalidateOnFocus: false,
    revalidateOnReconnect: false, // Prevent revalidation on reconnect
    revalidateIfStale: false, // Don't revalidate stale data automatically
    onError: (err) => {
      console.error('Failed to load exchange credentials:', err);
    },
  });
  
  const { data: walletsResponse, isLoading: loadingWallets, error: walletsError, mutate: mutateWallets } = useSWR('/admin/whitelisted-wallets', swrFetcher, {
    refreshInterval: 0,
    revalidateOnFocus: false,
    revalidateOnReconnect: false, // Prevent revalidation on reconnect
    revalidateIfStale: false, // Don't revalidate stale data automatically
    onError: (err) => {
      console.error('Failed to load whitelisted wallets:', err);
    },
  });

  const isLoading = loadingCreds || loadingWallets;
  const error = credsError || walletsError;
  const mutate = () => { mutateCreds(); mutateWallets(); };
  
  // Merge backend data with DEFAULT_EXCHANGES to always show all exchanges
  // Use stable references to prevent unnecessary recalculations
  const displayExchanges = useMemo(() => {
    const result = { ...DEFAULT_EXCHANGES };
    
    // Merge credentials from backend
    const credentials = credentialsResponse?.exchanges || [];
    credentials.forEach(cred => {
      const exchangeId = cred.exchangeId?.toLowerCase();
      if (exchangeId && result[exchangeId]) {
        result[exchangeId] = {
          ...result[exchangeId],
          hasApiKey: true,
          enabled: cred.isEnabled,
          label: cred.label,
          apiKeyPreview: cred.apiKeyPreview,
          apiWalletAddress: cred.apiWalletAddress,
          acceptedStablecoins: cred.acceptedStablecoins || null,
          extraInfo: cred.extraInfo || null,
          hasPassphrase: cred.hasPassphrase,
        };
      }
    });

    // Merge deposit addresses from whitelisted wallets
    const wallets = walletsResponse?.wallets || [];
    wallets.forEach(wallet => {
      const exchangeId = wallet.exchange_id?.toLowerCase();
      if (exchangeId && result[exchangeId] && wallet.wallet_type === 'deposit') {
        if (!result[exchangeId].depositAddresses) {
          result[exchangeId].depositAddresses = {};
        }
        if (wallet.chain) {
          result[exchangeId].depositAddresses[wallet.chain] = wallet.address;
        }
      }
    });

    return result;
  }, [
    // Use stable references - only recalculate when data actually changes
    credentialsResponse?.exchanges,
    walletsResponse?.wallets,
  ]);

  const [selectedExchange, setSelectedExchange] = useState(null);
  const [testingExchange, setTestingExchange] = useState(null);
  const [testResults, setTestResults] = useState({});

  const handleTestConnection = async (exchange, e) => {
    e.stopPropagation(); // Don't open modal
    setTestingExchange(exchange);
    try {
      await testExchangeConnection(exchange);
      setTestResults(prev => ({ ...prev, [exchange]: { success: true } }));
    } catch (err) {
      setTestResults(prev => ({ ...prev, [exchange]: { success: false } }));
    } finally {
      setTestingExchange(null);
    }
  };

  if (isLoading) {
    return (
      <div className="exchange-section">
        <div className="section-header"><h3>Exchanges</h3></div>
        <div className="loading-state compact"><div className="loading-spinner"></div></div>
      </div>
    );
  }

  const exchangeList = displayExchanges ? Object.entries(displayExchanges) : [];
  const showApiWarning = error && !credentialsResponse;

  const CLOUD_RUN_IP = '34.84.148.123';

  return (
    <div className="exchange-section">
      <div className="section-header">
        <div className="header-title-with-ip">
          <h3>Exchanges</h3>
          <div className="ip-whitelist-info">
            <span className="ip-whitelist-label">IP address to whitelist on Exchanges:</span>
            <span className="ip-whitelist-value mono">{CLOUD_RUN_IP}</span>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(CLOUD_RUN_IP, 'IP Address');
              }} 
              className="btn-copy-ip-inline"
              title="Copy IP address"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>
        <div className="header-actions">
          {showApiWarning && (
            <span className="api-warning" title="Backend unavailable - showing placeholders">
              Offline
            </span>
          )}
          <button onClick={() => mutate()} className="btn-refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className="exchange-tiles">
        {exchangeList.map(([name, config]) => {
          const testResult = testResults[name];
          const isTesting = testingExchange === name;
          const hasDeposit = config.depositAddresses && Object.keys(config.depositAddresses).length > 0;
          const depositAddressList = config.depositAddresses ? Object.entries(config.depositAddresses) : [];

          return (
            <div 
              key={name} 
              className={`exchange-tile clickable ${!config.hasApiKey ? 'not-configured' : ''}`}
              onClick={() => { 
                console.log('[DEBUG] Opening modal for', name, 'config:', config);
                setSelectedExchange({ name, config });
              }}
            >
              {/* Tile Header */}
              <div className="tile-header">
                <span className="tile-name">{name.charAt(0).toUpperCase() + name.slice(1)}</span>
              </div>

              {/* Link Account button when not configured */}
              {!config.hasApiKey && (
                <button className="btn-link-account">
                  Link Account
                </button>
              )}

              {/* Deposit Address status + Test button on same line */}
                <div className="tile-status-row">
                  <div className="tile-status">
                  <span className={`status-dot ${hasDeposit ? 'active' : 'warning'}`}></span>
                  <span className="status-text">Deposit Address</span>
                  </div>
                <div className="test-btn-wrapper">
                  <span 
                    className={`test-dot ${testResult ? (testResult.success ? 'success' : 'error') : 'default'}`} 
                  ></span>
                  <button
                    onClick={(e) => handleTestConnection(name, e)}
                    className="btn-test-sm"
                    disabled={isTesting || !config.hasApiKey}
                  >
                    {isTesting ? '...' : 'Test'}
                  </button>
                </div>
                  </div>

              {/* Show deposit addresses if configured */}
              {depositAddressList.length > 0 && (
                <div className="tile-addresses">
                  {depositAddressList.slice(0, 2).map(([chain, addr]) => (
                    <div key={chain} className="address-row">
                      <button 
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(addr); }} 
                        className="btn-copy-xs"
                      >
                        <Copy size={10} />
                    </button>
                      <span className="chain-badge">{chain.toUpperCase().slice(0, 3)}</span>
                      <span className="address-short mono">{shortenAddress(addr)}</span>
                  </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {exchangeList.length === 0 && (
          <div className="empty-state-sm">No exchanges configured</div>
        )}
      </div>

      {/* Exchange Config Modal */}
      {selectedExchange && (
        <ExchangeConfigModal
          key={selectedExchange.name} // Use key to force remount when exchange changes, preventing stale state
          exchange={selectedExchange.name}
          exchangeData={selectedExchange.config}
          onClose={() => setSelectedExchange(null)}
          onSave={() => {
            mutate();
            setSelectedExchange(null); // Close modal after save
          }}
        />
      )}
    </div>
  );
}

// Multi-Select Network Component
function MultiSelectNetworks({ selectedChains, onChange, availableChains }) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (chainId) => {
    if (selectedChains.includes(chainId)) {
      onChange(selectedChains.filter(c => c !== chainId));
    } else {
      onChange([...selectedChains, chainId]);
    }
  };

  const selectedLabels = selectedChains.map(chainId => {
    const chain = availableChains.find(c => c.id === chainId);
    return chain ? chain.short : chainId.toUpperCase();
  });

  return (
    <div className="multi-select-wrapper">
      <div 
        className={`multi-select-input ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedChains.length > 0 ? (
          <div className="selected-chips">
            {selectedLabels.map((label, idx) => (
              <span key={idx} className="selected-chip">
                {label}
                <button
                  type="button"
                  className="chip-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    const chainId = availableChains.find(c => c.short === label)?.id || selectedChains[idx];
                    handleToggle(chainId);
                  }}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="multi-select-placeholder">Select networks...</span>
        )}
        <ChevronDown size={16} className={`chevron ${isOpen ? 'open' : ''}`} />
      </div>
      {isOpen && (
        <div className="multi-select-dropdown">
          {/* EVM Chains Section */}
          <div className="chain-group">
            <div className="chain-group-header">EVM Chains</div>
            <div className="chain-group-items">
              {availableChains.filter(c => c.type === 'evm').map(chain => (
                <label key={chain.id} className="multi-select-option">
                  <input
                    type="checkbox"
                    checked={selectedChains.includes(chain.id)}
                    onChange={() => handleToggle(chain.id)}
                  />
                  <span className={`option-label ${selectedChains.includes(chain.id) ? 'selected' : ''}`}>
                    <span className="chain-color-dot" style={{ backgroundColor: chain.color }}></span>
                    <span className="chain-name">{chain.label}</span>
                    <span className="chain-short">{chain.short}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Non-EVM Chains Section */}
          <div className="chain-group">
            <div className="chain-group-header">Non-EVM Chains</div>
            <div className="chain-group-items">
              {availableChains.filter(c => c.type === 'non-evm').map(chain => (
                <label key={chain.id} className="multi-select-option">
                  <input
                    type="checkbox"
                    checked={selectedChains.includes(chain.id)}
                    onChange={() => handleToggle(chain.id)}
                  />
                  <span className={`option-label ${selectedChains.includes(chain.id) ? 'selected' : ''}`}>
                    <span className="chain-color-dot" style={{ backgroundColor: chain.color }}></span>
                    <span className="chain-name">{chain.label}</span>
                    <span className="chain-short">{chain.short}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Treasury Wallets Component - Tile Layout like Exchanges
function TreasuryWallets() {
  const { data: walletData, isLoading, mutate } = useSWR('/admin/treasury-wallets', swrFetcher, {
    refreshInterval: 0,
    revalidateOnFocus: false, // Disable to prevent form resets when user is typing
    revalidateOnReconnect: false, // Disable to prevent form resets
    revalidateIfStale: false, // Don't revalidate stale data automatically
  });
  
  // Fetch wallet metadata from database
  const { data: walletMetadataData, mutate: mutateMetadata } = useSWR(
    '/admin/treasury-wallets/metadata', 
    swrFetcher, 
    {
      refreshInterval: 0,
      revalidateOnFocus: false, // Disable to prevent form resets when user is typing
      revalidateOnReconnect: false, // Disable to prevent form resets
      revalidateIfStale: false, // Don't revalidate stale data automatically
    }
  );

  // Available chains - grouped by type
  const availableChains = [
    // EVM Chains
    { id: 'arbitrum', label: 'Arbitrum', short: 'ARB', type: 'evm', color: '#28a0f0' },
    { id: 'ethereum', label: 'Ethereum', short: 'ETH', type: 'evm', color: '#627eea' },
    { id: 'polygon', label: 'Polygon', short: 'MATIC', type: 'evm', color: '#8247e5' },
    { id: 'base', label: 'Base', short: 'BASE', type: 'evm', color: '#0052ff' },
    { id: 'optimism', label: 'Optimism', short: 'OP', type: 'evm', color: '#ff0420' },
    { id: 'avalanche', label: 'Avalanche', short: 'AVAX', type: 'evm', color: '#e84142' },
    { id: 'bsc', label: 'BSC', short: 'BSC', type: 'evm', color: '#f3ba2f' },
    // Non-EVM Chains
    { id: 'solana', label: 'Solana', short: 'SOL', type: 'non-evm', color: '#14f195' },
    { id: 'bitcoin', label: 'Bitcoin', short: 'BTC', type: 'non-evm', color: '#f7931a' },
  ];

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingWallet, setEditingWallet] = useState(null);
  const [newWallet, setNewWallet] = useState({ 
    name: '', 
    address: '', 
    chains: ['arbitrum'], 
    type: 'eoa',
    walletType: 'eoa',
    isMain: false,
    isIntermediary: false,
    safeAddress: '',
    privateKey: ''
  });
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [addError, setAddError] = useState(null);
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false);
  const [generatedWallet, setGeneratedWallet] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Transform backend data to frontend structure (by address with multiple chains)
  const walletsList = useMemo(() => {
    // Use new format if available, otherwise fall back to old format
    const configuredByAddress = walletData?.configuredByAddress || {};
    const configuredByChain = walletData?.configured || {}; // Old format for backward compatibility
    const envAddress = walletData?.envWalletAddress?.toLowerCase();
    const defaultWallet = walletData?.defaultWallet?.toLowerCase() || envAddress;
    
    // Debug logging
    console.log('[TreasuryWallets] walletData:', walletData);
    console.log('[TreasuryWallets] configuredByAddress:', configuredByAddress);
    console.log('[TreasuryWallets] configuredByChain (old):', configuredByChain);
    console.log('[TreasuryWallets] walletMetadataData:', walletMetadataData);
    
    // Load wallet metadata from API (fallback to empty object if not loaded yet)
    const walletMetadata = walletMetadataData || {};
    
    // Group wallets by address
    const walletsMap = new Map();
    
    // Process new format: address -> {chains, type}
    if (Object.keys(configuredByAddress).length > 0) {
      Object.entries(configuredByAddress).forEach(([address, wallet]) => {
        const addr = address.toLowerCase();
        walletsMap.set(addr, {
          address: address, // Use original case from config
          name: walletMetadata[addr]?.name || '',
          chains: [...(wallet.chains || [])],
          type: wallet.type || 'eoa',
          walletType: walletMetadata[addr]?.walletType || 'eoa',
          isPrimary: addr === envAddress,
          isDefault: addr === defaultWallet,
          isMain: walletMetadata[addr]?.isMain || (addr === defaultWallet),
          isIntermediary: walletMetadata[addr]?.isIntermediary || false,
          safeAddress: walletMetadata[addr]?.safeAddress || null,
          hasPrivateKey: walletMetadata[addr]?.hasPrivateKey || false,
        });
      });
    } else {
      // Fallback to old format: chain -> {address, type}
      Object.entries(configuredByChain).forEach(([chain, wallet]) => {
        const addr = wallet.address?.toLowerCase();
        if (!addr) {
          console.warn('[TreasuryWallets] Wallet without address found:', { chain, wallet });
          return;
        }
        
        if (!walletsMap.has(addr)) {
          walletsMap.set(addr, {
            address: wallet.address,
            name: walletMetadata[addr]?.name || '',
            chains: [],
            type: wallet.type || 'eoa',
            walletType: walletMetadata[addr]?.walletType || 'eoa',
            isPrimary: addr === envAddress,
            isDefault: addr === defaultWallet,
            isMain: walletMetadata[addr]?.isMain || (addr === defaultWallet),
            isIntermediary: walletMetadata[addr]?.isIntermediary || false,
            safeAddress: walletMetadata[addr]?.safeAddress || null,
            hasPrivateKey: walletMetadata[addr]?.hasPrivateKey || false,
          });
        }
        walletsMap.get(addr).chains.push(chain);
      });
    }
    
    // Also include wallets that have metadata but no chains configured
    // This prevents wallets from disappearing when they lose all chains
    Object.keys(walletMetadata).forEach((addr) => {
      const normalizedAddr = addr.toLowerCase();
      if (!walletsMap.has(normalizedAddr)) {
        walletsMap.set(normalizedAddr, {
          address: addr, // Use original case from metadata
          name: walletMetadata[addr]?.name || '',
          chains: [],
          type: 'eoa', // Default type
          walletType: walletMetadata[addr]?.walletType || 'eoa',
          isPrimary: normalizedAddr === envAddress,
          isDefault: normalizedAddr === defaultWallet,
          isMain: walletMetadata[addr]?.isMain || (normalizedAddr === defaultWallet),
          isIntermediary: walletMetadata[addr]?.isIntermediary || false,
          safeAddress: walletMetadata[addr]?.safeAddress || null,
          hasPrivateKey: walletMetadata[addr]?.hasPrivateKey || false,
        });
      }
    });
    
    // Add primary wallet if not in configured and not in metadata
    if (envAddress && !walletsMap.has(envAddress)) {
      walletsMap.set(envAddress, {
        address: walletData.envWalletAddress,
        name: walletMetadata[envAddress]?.name || '',
        chains: ['arbitrum'], // Default
        type: 'eoa',
        walletType: walletMetadata[envAddress]?.walletType || 'eoa',
        isPrimary: true,
        isDefault: envAddress === defaultWallet,
        isMain: walletMetadata[envAddress]?.isMain || (envAddress === defaultWallet),
        isIntermediary: walletMetadata[envAddress]?.isIntermediary || false,
        safeAddress: walletMetadata[envAddress]?.safeAddress || null,
        hasPrivateKey: walletMetadata[envAddress]?.hasPrivateKey || false,
      });
    }
    
    const result = Array.from(walletsMap.values());
    
    // Sort wallets: main wallet first, then by name, then by address
    result.sort((a, b) => {
      // Main wallet always first
      if (a.isMain && !b.isMain) return -1;
      if (!a.isMain && b.isMain) return 1;
      // If both are main or neither is main, sort by name (if available), then by address
      if (a.name && !b.name) return -1;
      if (!a.name && b.name) return 1;
      if (a.name && b.name) {
        const nameCompare = a.name.localeCompare(b.name);
        if (nameCompare !== 0) return nameCompare;
      }
      // Finally sort by address
      return a.address.localeCompare(b.address);
    });
    
    console.log('[TreasuryWallets] Final walletsList:', result);
    
    return result;
  }, [walletData, walletMetadataData]);

  // Use all wallets in a single list (proxy wallets will show with a badge)
  const allWalletsList = useMemo(() => {
    return walletsList;
  }, [walletsList]);

  const handleDeleteWallet = async (wallet) => {
    const walletDisplayName = wallet.name || shortenAddress(wallet.address);
    const confirmMessage = `Are you sure you want to delete the wallet "${walletDisplayName}"?\n\nAddress: ${wallet.address}\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      // Delete all chains for this wallet
      for (const chain of wallet.chains) {
        await api.delete(`/admin/treasury-wallets/${wallet.address}/${chain}`);
      }
      // Delete wallet metadata
      try {
        await api.delete(`/admin/treasury-wallets/metadata/${wallet.address}`);
      } catch (err) {
        // Metadata might not exist, ignore error
        console.warn('Failed to delete wallet metadata:', err);
      }
      
      // Longer delay and cache invalidation like in handleAddWallet
      await new Promise(resolve => setTimeout(resolve, 500));
      mutate(undefined, { revalidate: false });
      mutateMetadata(undefined, { revalidate: false });
      await new Promise(resolve => setTimeout(resolve, 100));
      await Promise.all([
        mutate(undefined, { revalidate: true }),
        mutateMetadata(undefined, { revalidate: true })
      ]);
    } catch (err) {
      alert(`Failed: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleEditWallet = (wallet) => {
    const metadata = walletMetadataData?.[wallet.address.toLowerCase()] || {};
    setEditingWallet({
      ...wallet,
      hasPrivateKey: metadata.hasPrivateKey || false
    });
    setNewWallet({ 
      name: wallet.name || '', 
      address: wallet.address, 
      chains: [...wallet.chains], 
      type: wallet.type,
      walletType: metadata.walletType || 'eoa',
      isMain: wallet.isMain || false,
      isIntermediary: metadata.isIntermediary || false,
      safeAddress: metadata.safeAddress || '',
      privateKey: '' // Never load private key for security
    });
    setAddError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingWallet || !newWallet.address.trim() || newWallet.chains.length === 0) return;
    
    setIsSaving(true);
    setAddError(null);

    try {
      const oldChains = editingWallet.chains;
      const newChains = newWallet.chains;
      
      // Remove chains that are no longer in the new list
      for (const chain of oldChains) {
        if (!newChains.includes(chain)) {
          // Delete chain from this specific wallet
          await api.delete(`/admin/treasury-wallets/${newWallet.address.trim()}/${chain}`);
        }
      }
      
      // Add new chains (no reassignment needed - wallets are independent)
      for (const chain of newChains) {
        if (!oldChains.includes(chain)) {
          await api.post('/admin/treasury-wallets', {
            address: newWallet.address.trim(),
            chain,
            type: newWallet.type,
          });
        }
      }
      
      // Update type if changed (need to update all chains)
      // Note: Type is stored per wallet, so we need to remove and re-add all chains with new type
      if (newWallet.type !== editingWallet.type) {
        for (const chain of newChains) {
          await api.delete(`/admin/treasury-wallets/${newWallet.address.trim()}/${chain}`);
          await api.post('/admin/treasury-wallets', {
            address: newWallet.address.trim(),
            chain,
            type: newWallet.type,
          });
        }
      }

      // Save wallet metadata (name, isMain, walletType, isIntermediary, safeAddress, privateKey) to database
      const metadataPayload = {
        name: newWallet.name || null,
        isMain: newWallet.isMain,
        isIntermediary: newWallet.isIntermediary,
        walletType: newWallet.walletType,
      };
      
      // For Safe wallets, safeAddress is the same as address (the Safe contract address)
      if (newWallet.walletType === 'safe') {
        metadataPayload.safeAddress = newWallet.address;
      }
      
      // Only include privateKey if provided (for new wallets or when updating)
      if (newWallet.privateKey) {
        metadataPayload.privateKey = newWallet.privateKey;
      }
      
      const response = await api.put(`/admin/treasury-wallets/metadata/${newWallet.address}`, metadataPayload);
      
      await mutate();
      await mutateMetadata();
      
      // Check for warning in response (e.g., permission denied for Secret Manager)
      if (response.data?.warning) {
        alert(`⚠️ Warning: ${response.data.warning}\n\n${response.data.message || ''}`);
      } else if (response.data?.message && response.data.message.includes('could not be stored')) {
        alert(`⚠️ Warning: ${response.data.message}\n\n${response.data.warning || ''}`);
      }
      
      setEditingWallet(null);
      setNewWallet({ name: '', address: '', chains: ['arbitrum'], type: 'eoa', walletType: 'eoa', isMain: false, isIntermediary: false, safeAddress: '', privateKey: '' });
    } catch (err) {
      setAddError(err.response?.data?.error || err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddWallet = async () => {
    if (!newWallet.address.trim() || newWallet.chains.length === 0) return;
    
    setIsAdding(true);
    setAddError(null);

    try {
      const errors = [];
      
      // Add wallet for each chain
      for (const chain of newWallet.chains) {
        try {
          await api.post('/admin/treasury-wallets', {
            address: newWallet.address.trim(),
            chain,
            type: newWallet.type,
          });
        } catch (chainErr) {
          const chainErrorMsg = chainErr.response?.data?.error || chainErr.message || 'Unknown error';
          errors.push(`${chain}: ${chainErrorMsg}`);
          console.error(`Failed to add wallet for chain ${chain}:`, chainErr);
        }
      }

      if (errors.length > 0) {
        throw new Error(`Failed to add wallet for some chains:\n${errors.join('\n')}`);
      }

      // Save wallet metadata (name, isMain, walletType, isIntermediary, safeAddress, privateKey) to database
      try {
        const metadataPayload = {
          name: newWallet.name || null,
          isMain: newWallet.isMain,
          isIntermediary: newWallet.isIntermediary,
          walletType: newWallet.walletType,
        };
        
        // For Safe wallets, safeAddress is the same as address (the Safe contract address)
        if (newWallet.walletType === 'safe') {
          metadataPayload.safeAddress = newWallet.address;
        }
        
        // Only include privateKey if provided
        if (newWallet.privateKey) {
          metadataPayload.privateKey = newWallet.privateKey;
        }
        
        const metadataResponse = await api.put(`/admin/treasury-wallets/metadata/${newWallet.address}`, metadataPayload);
        
        // Check for warning in response (e.g., permission denied for Secret Manager)
        if (metadataResponse.data?.warning) {
          alert(`⚠️ Warning: ${metadataResponse.data.warning}\n\n${metadataResponse.data.message || ''}`);
        } else if (metadataResponse.data?.message && metadataResponse.data.message.includes('could not be stored')) {
          alert(`⚠️ Warning: ${metadataResponse.data.message}\n\n${metadataResponse.data.warning || ''}`);
        }
      } catch (metadataErr) {
        console.warn('Failed to save wallet metadata:', metadataErr);
        // Don't fail the whole operation if metadata save fails, but show error if it's a permission issue
        if (metadataErr.response?.data?.error?.includes('PERMISSION_DENIED') || 
            metadataErr.response?.data?.error?.includes('Permission denied')) {
          alert(`⚠️ Warning: ${metadataErr.response.data.error}\n\nTo complete setup, manually create the secret in Google Secret Manager.`);
        }
      }

      // Longer delay to ensure backend has fully processed all changes and database is updated
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force refresh both data sources - use mutate with revalidate and also trigger a manual refetch
      // First invalidate the cache, then wait a bit, then refetch
      mutate(undefined, { revalidate: false }); // Invalidate cache
      mutateMetadata(undefined, { revalidate: false }); // Invalidate cache
      
      // Wait a bit more for cache invalidation
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Now force a fresh fetch by calling mutate with revalidate: true
      const [walletResult, metadataResult] =       await Promise.all([
        mutate(undefined, { revalidate: true }),
        mutateMetadata(undefined, { revalidate: true })
      ]);
      
      setShowAddForm(false);
      setNewWallet({ name: '', address: '', chains: ['arbitrum'], type: 'eoa', walletType: 'eoa', isMain: false, isIntermediary: false, safeAddress: '', privateKey: '' });
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown error';
      console.error('Failed to add wallet:', err);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: errorMessage,
        fullError: err,
      });
      setAddError(errorMessage);
      alert(`Failed to add wallet: ${errorMessage}`);
    } finally {
      setIsAdding(false);
    }
  };

  const handleGenerateWallet = async () => {
    setIsGenerating(true);
    setAddError(null);
    try {
      const response = await api.post('/admin/treasury-wallets/generate');
      const { address, privateKey } = response.data;
      
      // Set the generated wallet data
      setGeneratedWallet({ address, privateKey });
      
      // Auto-populate the form
      setNewWallet(prev => ({
        ...prev,
        address: address,
        privateKey: privateKey,
        walletType: 'eoa', // Generated wallets are always EOA
      }));
      
      // Show the add form so user can see the generated wallet
      setShowAddForm(true);
      
      // Show the private key modal
      setShowPrivateKeyModal(true);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to generate wallet';
      console.error('Failed to generate wallet:', err);
      setAddError(errorMessage);
      alert(`Failed to generate wallet: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="treasury-section">
        <div className="section-header"><h3>Wallets</h3></div>
        <div className="loading-state compact"><div className="loading-spinner"></div></div>
      </div>
    );
  }

  return (
    <>
      {/* Wallets Section */}
      <div className="treasury-section">
        <div className="section-header">
          <h3>Wallets</h3>
          {!showAddForm && !editingWallet && allWalletsList.length > 0 && (
            <div className="header-actions">
              <button onClick={() => setShowAddForm(true)} className="btn-empty-state btn-header-action">
                Add Wallet
            </button>
              <button 
                onClick={handleGenerateWallet}
                disabled={isGenerating}
                className="btn-empty-state btn-header-action"
              >
                {isGenerating ? '...' : 'Create New'}
              </button>
            </div>
          )}
        </div>

        <div className="wallet-tiles-grid">
          {/* Empty State - Show two options when no wallets */}
          {allWalletsList.length === 0 && !showAddForm && !editingWallet && (
            <div className="wallet-empty-state">
              <div className="empty-state-content">
                <p className="empty-state-label">No wallets configured</p>
                <div className="empty-state-actions">
                  <button 
                    onClick={() => setShowAddForm(true)}
                    className="btn-empty-state"
                  >
                    I already have a wallet
                  </button>
                  <button 
                    onClick={handleGenerateWallet}
                    disabled={isGenerating}
                    className="btn-empty-state"
                  >
                    {isGenerating ? '...' : "I don't have a wallet, create one for me"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Wallet Tiles - Like Exchange Tiles */}
          {allWalletsList.map((wallet) => {
          const isEditing = editingWallet?.address === wallet.address;
          
          return (
            <div key={wallet.address} className={`wallet-tile-exchange ${isEditing ? 'editing' : ''}`}>
              {!isEditing ? (
                <>
                  <div className="tile-header">
                    <div className="tile-name-wrapper">
                      <span className="tile-name">{wallet.name || shortenAddress(wallet.address)}</span>
                      {wallet.isIntermediary && <span className="intermediary-wallet-badge">PROXY</span>}
                    </div>
                    <div className="tile-actions">
                      <button onClick={() => handleEditWallet(wallet)} className="btn-icon-sm" title="Edit"><Pencil size={12} /></button>
                      <button onClick={() => handleDeleteWallet(wallet)} className="btn-icon-sm" title="Delete"><X size={14} /></button>
                    </div>
                  </div>
                  
                  <div className="tile-status">
                    <span className="status-text">
                      {wallet.isDefault ? 'Main Wallet' : wallet.isPrimary ? 'Primary' : 'Configured'}
                      {wallet.walletType === 'safe' && ' • Safe'}
                    </span>
                  </div>

                  <div className="chain-tags">
                    {wallet.chains.map(chain => {
                      const chainInfo = availableChains.find(c => c.id === chain);
                      return (
                        <span key={chain} className={`chain-tag ${chain}`}>
                          {chainInfo ? chainInfo.short : chain.toUpperCase()}
                        </span>
                      );
                    })}
                  </div>

                  <div className="tile-address">
                    <button onClick={() => copyToClipboard(wallet.address)} className="btn-copy-xs"><Copy size={10} /></button>
                    <span className="address-short mono" title={wallet.address}>
                      {shortenAddress(wallet.address)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="tile-edit-form">
                  {addError && <div className="form-error-sm">{addError}</div>}
                  
                  <input
                    type="text"
                    value={newWallet.name}
                    onChange={(e) => setNewWallet({ ...newWallet, name: e.target.value })}
                    placeholder="Wallet name..."
                    className="edit-input"
                  />
                  
                  <input
                    type="text"
                    value={newWallet.address}
                    onChange={(e) => setNewWallet({ ...newWallet, address: e.target.value })}
                    placeholder={newWallet.walletType === 'safe' ? "0x... (Safe contract address)" : "0x..."}
                    className="edit-input"
                    disabled={wallet.isPrimary}
                  />
                  
                  <div className="edit-section">
                    <div className="edit-section-title">Networks</div>
                    <MultiSelectNetworks
                      selectedChains={newWallet.chains}
                      onChange={(chains) => setNewWallet({ ...newWallet, chains })}
                      availableChains={availableChains}
                    />
                  </div>

                  <div className="edit-section">
                    <div className="edit-section-title">Wallet Type</div>
                    <select
                      value={newWallet.walletType}
                      onChange={(e) => setNewWallet({ ...newWallet, walletType: e.target.value })}
                      className="edit-select"
                    >
                      <option value="eoa">EOA (Externally Owned Account)</option>
                      <option value="safe">Safe Wallet (Gnosis Safe)</option>
                    </select>
                  </div>


                  <div className="edit-section">
                    <div className="edit-section-title">Private Key (Server Signer)</div>
                    <input
                      type="password"
                      value={newWallet.privateKey}
                      onChange={(e) => setNewWallet({ ...newWallet, privateKey: e.target.value })}
                      placeholder={editingWallet?.hasPrivateKey ? "Leave empty to keep existing key" : "0x... (required for Proxy wallets)"}
                      className="edit-input"
                    />
                    <div className="edit-hint">
                      {editingWallet?.hasPrivateKey 
                        ? "Private key already stored. Leave empty to keep existing key, or enter new key to update."
                        : "Private key will be stored securely in Google Secret Manager. Required if marking as Proxy wallet."}
                    </div>
                  </div>

                  <div className="edit-section switch-row">
                    <span className="switch-label">Proxy Wallet (for transfers)</span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={newWallet.isIntermediary}
                        onChange={(e) => setNewWallet({ ...newWallet, isIntermediary: e.target.checked })}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div className="edit-actions-row">
                    <button onClick={() => { setEditingWallet(null); setNewWallet({ name: '', address: '', chains: ['arbitrum'], type: 'eoa', walletType: 'eoa', isMain: false, isIntermediary: false, safeAddress: '', privateKey: '' }); }} className="btn-cancel-sm">Cancel</button>
                    <button onClick={handleSaveEdit} className="btn-save-sm" disabled={isSaving || newWallet.chains.length === 0}>
                      {isSaving ? '...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add Wallet Tile */}
        {showAddForm && (
          <div className="wallet-tile-exchange add-form">
            <button 
              onClick={() => { setShowAddForm(false); setNewWallet({ name: '', address: '', chains: ['arbitrum'], type: 'eoa', walletType: 'eoa', isMain: false, isIntermediary: false, safeAddress: '', privateKey: '' }); }} 
              className="btn-close-tile"
            >
              <X size={14} />
            </button>
            <div className="tile-form">
              {addError && <div className="form-error-inline">{addError}</div>}
              
              <input
                type="text"
                value={newWallet.name}
                onChange={(e) => setNewWallet({ ...newWallet, name: e.target.value })}
                placeholder="Wallet name..."
                className="edit-input"
              />
              
              <input
                type="text"
                value={newWallet.address}
                onChange={(e) => setNewWallet({ ...newWallet, address: e.target.value })}
                placeholder={newWallet.walletType === 'safe' ? "0x... (Safe contract address)" : "0x..."}
                className="edit-input"
              />
              
              <div className="edit-section">
                <div className="edit-section-title">Networks</div>
                <MultiSelectNetworks
                  selectedChains={newWallet.chains}
                  onChange={(chains) => setNewWallet({ ...newWallet, chains })}
                  availableChains={availableChains}
                />
              </div>

              <div className="edit-section">
                <div className="edit-section-title">Wallet Type</div>
                <select
                  value={newWallet.walletType}
                  onChange={(e) => setNewWallet({ ...newWallet, walletType: e.target.value })}
                  className="edit-select"
                >
                  <option value="eoa">EOA (Externally Owned Account)</option>
                  <option value="safe">Safe Wallet (Gnosis Safe)</option>
                </select>
              </div>


              <div className="edit-section">
                <div className="edit-section-title">Private Key (Server Signer)</div>
                <input
                  type="password"
                  value={newWallet.privateKey}
                  onChange={(e) => setNewWallet({ ...newWallet, privateKey: e.target.value })}
                  placeholder="0x... (required for Proxy wallets)"
                  className="edit-input"
                />
                <div className="edit-hint">Private key will be stored securely in Google Secret Manager. Required if marking as Proxy wallet.</div>
              </div>

              <div className="edit-section switch-row">
                <span className="switch-label">Proxy Wallet (for transfers)</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={newWallet.isIntermediary}
                    onChange={(e) => setNewWallet({ ...newWallet, isIntermediary: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="edit-actions-row">
                <button onClick={handleAddWallet} className="btn-save-sm" disabled={isAdding || !newWallet.address.trim() || newWallet.chains.length === 0}>
                  {isAdding ? '...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Private Key Modal - Show only once after generation */}
      {showPrivateKeyModal && generatedWallet && (
        <div className="modal-overlay" onClick={() => setShowPrivateKeyModal(false)}>
          <div className="modal-content private-key-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Save Your Private Key</h3>
              <button onClick={() => setShowPrivateKeyModal(false)} className="btn-close-modal">
                <X size={18} />
              </button>
        </div>
            <div className="modal-body">
              <div className="warning-box">
                <p><strong>IMPORTANT:</strong> This is the only time you will see your private key. Please copy it to a secure location.</p>
                <ul>
                  <li>Never share your private key with anyone</li>
                  <li>Store it in a secure password manager or encrypted file</li>
                  <li>If you lose this key, you will lose access to this wallet</li>
                </ul>
            </div>
              
              <div className="private-key-display">
                <div className="field-group">
                  <label>Address:</label>
                  <div className="value-with-copy">
                    <code className="mono">{generatedWallet.address}</code>
                    <button 
                      onClick={() => copyToClipboard(generatedWallet.address, 'Address')}
                      className="btn-copy-inline"
                      title="Copy address"
                    >
                      <Copy size={14} />
                    </button>
                        </div>
                      </div>
                      
                <div className="field-group">
                  <label>Private Key:</label>
                  <div className="value-with-copy">
                    <code className="mono private-key-value">{generatedWallet.privateKey}</code>
                    <button 
                      onClick={() => copyToClipboard(generatedWallet.privateKey, 'Private Key')}
                      className="btn-copy-inline"
                      title="Copy private key"
                    >
                      <Copy size={14} />
                    </button>
                      </div>
                        </div>
                      </div>
                      </div>
            <div className="modal-footer">
              <button 
                onClick={() => {
                  copyToClipboard(generatedWallet.privateKey, 'Private Key');
                  alert('Private key copied to clipboard!');
                }}
                className="btn-primary"
              >
                Copy Private Key
              </button>
              <button 
                onClick={() => setShowPrivateKeyModal(false)}
                className="btn-secondary"
              >
                I've Saved It
                        </button>
                      </div>
                    </div>
                </div>
          )}
    </>
  );
}

// System Health Component - Inline compact version
function SystemHealth() {
  const { data: health, error, mutate } = useSWR('/health', swrFetcher, {
    refreshInterval: 30000, // Refresh every 30 seconds (not causing reloads, just data updates)
    revalidateOnFocus: false, // Disable to prevent reloads on focus
    revalidateOnReconnect: false,
    revalidateIfStale: false,
  });
  const isHealthy = health && !error;

  return (
    <div className="health-inline" onClick={() => mutate()}>
      <span className={`health-dot ${isHealthy ? 'healthy' : 'unhealthy'}`}></span>
      <span className="health-label">{isHealthy ? 'Connected' : 'Offline'}</span>
    </div>
  );
}

// Transfer Settings Component
function TransferSettings() {
  // Cancellation window settings
  const cancellationWindowStatus = useSWR('/admin/secrets/CANCELLATION_WINDOW_SECONDS', swrFetcher, { 
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
  });
  const cancellationWindowEnabledStatus = useSWR('/admin/secrets/CANCELLATION_WINDOW_ENABLED', swrFetcher, { 
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
  });

  // Treasury hold time settings
  const treasuryHoldTimeStatus = useSWR('/admin/secrets/TREASURY_HOLD_TIME_SECONDS', swrFetcher, { 
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
  });
  const treasuryHoldTimeEnabledStatus = useSWR('/admin/secrets/TREASURY_HOLD_TIME_ENABLED', swrFetcher, { 
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
  });

  const [cancellationWindowValue, setCancellationWindowValue] = useState('12');
  const [cancellationWindowEnabled, setCancellationWindowEnabled] = useState(true);
  const [treasuryHoldTimeValue, setTreasuryHoldTimeValue] = useState('60');
  const [treasuryHoldTimeEnabled, setTreasuryHoldTimeEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    // Load cancellation window settings
    if (cancellationWindowStatus?.data?.preview) {
      const preview = cancellationWindowStatus.data.preview;
      const parsed = parseInt(preview, 10);
      if (preview && !isNaN(parsed)) {
        setCancellationWindowValue(preview);
      }
    }
    if (cancellationWindowEnabledStatus?.data?.preview) {
      const enabled = cancellationWindowEnabledStatus.data.preview?.toLowerCase() === 'true';
      setCancellationWindowEnabled(enabled);
    }
  }, [cancellationWindowStatus?.data, cancellationWindowEnabledStatus?.data]);

  useEffect(() => {
    // Load treasury hold time settings
    if (treasuryHoldTimeStatus?.data?.preview) {
      const preview = treasuryHoldTimeStatus.data.preview;
      if (preview && !isNaN(parseInt(preview))) {
        setTreasuryHoldTimeValue(preview);
      }
    }
    if (treasuryHoldTimeEnabledStatus?.data?.preview) {
      const enabled = treasuryHoldTimeEnabledStatus.data.preview?.toLowerCase() === 'true';
      setTreasuryHoldTimeEnabled(enabled);
    }
  }, [treasuryHoldTimeStatus?.data, treasuryHoldTimeEnabledStatus?.data]);

  const handleSaveCancellationWindow = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    const seconds = parseInt(cancellationWindowValue, 10);
    if (isNaN(seconds) || seconds < 0 || seconds > 60) {
      setSaveError('Please enter a number between 0 and 60');
      setIsSaving(false);
      return;
    }

    try {
      await Promise.all([
        api.put('/admin/secrets/CANCELLATION_WINDOW_SECONDS', { value: seconds.toString() }),
        api.put('/admin/secrets/CANCELLATION_WINDOW_ENABLED', { value: cancellationWindowEnabled.toString() }),
      ]);
      cancellationWindowStatus?.mutate();
      cancellationWindowEnabledStatus?.mutate();
      setSaveError(null);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveTreasuryHoldTime = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    const seconds = parseInt(treasuryHoldTimeValue, 10);
    if (isNaN(seconds) || seconds < 0 || seconds > 300) {
      setSaveError('Please enter a number between 0 and 300');
      setIsSaving(false);
      return;
    }

    try {
      await Promise.all([
        api.put('/admin/secrets/TREASURY_HOLD_TIME_SECONDS', { value: seconds.toString() }),
        api.put('/admin/secrets/TREASURY_HOLD_TIME_ENABLED', { value: treasuryHoldTimeEnabled.toString() }),
      ]);
      treasuryHoldTimeStatus?.mutate();
      treasuryHoldTimeEnabledStatus?.mutate();
      setSaveError(null);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="exchange-section">
      <div className="section-header">
        <h3>Transfer Settings</h3>
      </div>
      
      <div className="exchange-tiles">
        {/* Cancellation Window Tile */}
        <div className="exchange-tile">
          <div className="tile-header">
            <span className="tile-name">Cancellation Window</span>
          </div>
          
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '12px', lineHeight: '1.4' }}>
            Time window before withdrawal orders are sent to exchanges. During this window, transfers can be cancelled.
          </div>

          <div className="setting-row with-input">
            <div className="setting-left">
              <span className="setting-label">Duration (seconds)</span>
              <input
                type="number"
                min="0"
                max="60"
                value={cancellationWindowValue}
                onChange={(e) => setCancellationWindowValue(e.target.value)}
                className="setting-input"
                placeholder="12"
                disabled={!cancellationWindowEnabled}
                style={{ width: '100px' }}
              />
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={cancellationWindowEnabled}
                onChange={(e) => setCancellationWindowEnabled(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>

          <button
            onClick={handleSaveCancellationWindow}
            disabled={isSaving}
            className="btn-link-account"
            style={{ marginTop: '8px' }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>

          {saveError && (
            <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '8px' }}>{saveError}</div>
          )}
          {cancellationWindowStatus?.data?.configured && !saveError && (
            <div style={{ fontSize: '12px', color: '#10b981', marginTop: '8px' }}>Setting saved</div>
          )}
        </div>

        {/* Treasury Hold Time Tile */}
        <div className="exchange-tile">
          <div className="tile-header">
            <span className="tile-name">Treasury Hold Time</span>
          </div>
          
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '12px', lineHeight: '1.4' }}>
            Wait time after funds are received at the treasury proxy before proceeding to deposit. If disabled, uses a 2-second minimum wait.
          </div>

          <div className="setting-row with-input">
            <div className="setting-left">
              <span className="setting-label">Duration (seconds)</span>
              <input
                type="number"
                min="0"
                max="300"
                value={treasuryHoldTimeValue}
                onChange={(e) => setTreasuryHoldTimeValue(e.target.value)}
                className="setting-input"
                placeholder="60"
                disabled={!treasuryHoldTimeEnabled}
                style={{ width: '100px' }}
              />
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={treasuryHoldTimeEnabled}
                onChange={(e) => setTreasuryHoldTimeEnabled(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>

          <button
            onClick={handleSaveTreasuryHoldTime}
            disabled={isSaving}
            className="btn-link-account"
            style={{ marginTop: '8px' }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>

          {saveError && (
            <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '8px' }}>{saveError}</div>
          )}
          {treasuryHoldTimeStatus?.data?.configured && !saveError && (
            <div style={{ fontSize: '12px', color: '#10b981', marginTop: '8px' }}>Setting saved</div>
          )}
        </div>
      </div>
    </div>
  );
}

// DeFi Infra Settings Component - Tile Style (RPC & Etherscan only, Alchemy is primary provider)
function DefiInfraSettings() {
  const arbitrumRpcStatus = useSWR('/admin/secrets/ARBITRUM_RPC_URL', swrFetcher, { revalidateOnFocus: false });
  const ethereumRpcStatus = useSWR('/admin/secrets/ETHEREUM_RPC_URL', swrFetcher, { revalidateOnFocus: false });
  const etherscanStatus = useSWR('/admin/secrets/ETHERSCAN_API_KEY', swrFetcher, { revalidateOnFocus: false });

  const [selectedInfra, setSelectedInfra] = useState(null);

  const arbitrumRpcConfigured = arbitrumRpcStatus?.data?.configured;
  const ethereumRpcConfigured = ethereumRpcStatus?.data?.configured;
  const etherscanConfigured = etherscanStatus?.data?.configured;

  const handleSave = () => {
    // Refresh all statuses
    arbitrumRpcStatus?.mutate();
    ethereumRpcStatus?.mutate();
    etherscanStatus?.mutate();
  };

  const infraTiles = [
    {
      id: 'rpc',
      name: 'RPC',
      configured: arbitrumRpcConfigured || ethereumRpcConfigured,
      status: arbitrumRpcConfigured && ethereumRpcConfigured ? 'Both Configured' : 
              arbitrumRpcConfigured ? 'Arbitrum Only' :
              ethereumRpcConfigured ? 'Ethereum Only' : 'Not Configured',
      statusClass: (arbitrumRpcConfigured && ethereumRpcConfigured) ? 'active' : 
                   (arbitrumRpcConfigured || ethereumRpcConfigured) ? 'warning' : 'inactive',
      data: {
        arbitrumRpcUrlPreview: arbitrumRpcStatus?.data?.preview,
        ethereumRpcUrlPreview: ethereumRpcStatus?.data?.preview,
      },
    },
    {
      id: 'etherscan',
      name: 'Etherscan',
      configured: etherscanConfigured,
      status: etherscanConfigured ? 'Configured' : 'Not Configured',
      statusClass: etherscanConfigured ? 'active' : 'inactive',
      preview: etherscanStatus?.data?.preview,
      data: {
        apiKeyPreview: etherscanStatus?.data?.preview,
      },
    },
  ];

  return (
    <div className="exchange-section">
      <div className="section-header">
        <h3>DeFi Infra Settings</h3>
      </div>

      <div className="exchange-tiles">
        {infraTiles.map((tile) => (
          <div
            key={tile.id}
            className={`exchange-tile clickable ${!tile.configured ? 'not-configured' : ''}`}
            onClick={() => setSelectedInfra(tile)}
          >
            <div className="tile-header">
              <span className="tile-name">{tile.name}</span>
            </div>

            {!tile.configured && (
              <button className="btn-link-account">
                Configure
              </button>
            )}

            <div className="tile-status-row">
              <div className="tile-status">
                <span className={`status-dot ${tile.statusClass}`}></span>
                <span className="status-text">{tile.status}</span>
              </div>
            </div>

            {tile.preview && (
              <div className="tile-preview">
                <span className="preview-label">Key:</span>
                <span className="preview-value mono">{tile.preview}</span>
              </div>
            )}

            {tile.id === 'rpc' && (arbitrumRpcConfigured || ethereumRpcConfigured) && (
              <div className="tile-preview" style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                (Arb & Eth)
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedInfra && (
        <InfraConfigModal
          infraType={selectedInfra.id}
          infraData={selectedInfra.data}
          onClose={() => setSelectedInfra(null)}
          onSave={() => {
            handleSave();
            setSelectedInfra(null);
          }}
        />
      )}
    </div>
  );
}


// Main WhitelistManager Component
function WhitelistManager() {
  return (
    <div className="whitelist-manager">
      <div className="manager-header">
        <div className="header-left">
          <h2>Admin Settings</h2>
          <span className="manager-subtitle">Manage wallets, exchanges, and system configuration</span>
        </div>
        <SystemHealth />
      </div>

      <div className="manager-content">
        <TreasuryWallets />
        <ExchangeConfig />
        <TransferSettings />
        <DefiInfraSettings />
      </div>
    </div>
  );
}

export default WhitelistManager;


